/**
 * Customized Tanks Builder (5th calculator) — single-page BOQ form.
 *
 * Full BOQ (~18 fields) → POST /customized-tank/calculate → the response carries
 * ONLY the Final B2B Price + the TDS (never any cost build-up). Save quotation →
 * Convert to Proposal reuses the shared Meeting-Mode flow.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { customizedTankApi, proposalsApi } from '@/api/calculators';
import type { CustomizedTankInput, CustomizedTankResult } from '@/api/calculators/types';
import { formatINR } from '@/utils/formatters';
import { readStoredSession, writeStoredSession } from './MeetingModeLauncher';
import { ConvertToProposalButton } from './_shared/ConvertToProposalButton';

const CONN_SIZES = ['3/4"', '1"', '1.25"', '1.5"', '2"', '2.5"', '3"', '4"', '6"'];
const MATERIALS = ['MS', 'SS304', 'SS316L', 'SS2205', 'GI'] as const;
const COATINGS = ['none', 'ceramic', 'passivation', 'epoxy'] as const;
const INSULATIONS = ['none', 'puf', 'rockwool'] as const;
const CLADDINGS = ['none', 'ppgi05', 'ppgi035', 'ss304_1.0', 'aluminium'] as const;
const CONN_MATERIALS = ['MS', 'SS304', 'SS316L'] as const;
const MANHOLE_SIZES = ['', '150', '400', '450', '500', '600', '800'];
const MANHOLE_MATS = ['MS', 'SS304', 'SS316'] as const;

type Conn = { size: string; count: number };

const DEFAULTS: CustomizedTankInput = {
  capacityL: 5000,
  diaMM: 1700,
  heightMM: 2200,
  material: 'SS316L',
  shellMM: 3,
  dishMM: 3,
  pressurized: false,
  coating: 'none',
  insulation: 'none',
  insulationMM: 0,
  outerCladding: 'none',
  connMaterial: 'SS304',
  connections: [],
  manholeSize: null,
  manholeMat: 'SS304',
  frame: 0,
  valves: 0,
  extras: 0,
  extrasDescription: '',
};

// 5 reference presets (verbatim from the delivered Sunstellar_Tank_Builder.html;
// projectMultiplier dropped — pricing is B2B-only).
const PRESETS: Array<{ name: string; cfg: CustomizedTankInput }> = [
  {
    name: '1500L MS Ceramic Press. +5 htr',
    cfg: { capacityL: 1500, diaMM: 1125, heightMM: 1250, material: 'MS', shellMM: 5, dishMM: 5, pressurized: true, coating: 'ceramic', insulation: 'puf', insulationMM: 50, outerCladding: 'ppgi05', connMaterial: 'MS', manholeSize: '400', manholeMat: 'MS', frame: 4500, valves: 3100, extras: 7500, extrasDescription: '5 × immersion heaters', connections: [{ size: '2"', count: 5 }, { size: '1.25"', count: 5 }, { size: '3/4"', count: 2 }, { size: '1.5"', count: 5 }] },
  },
  {
    name: '2500L MS Ceramic Press.',
    cfg: { capacityL: 2500, diaMM: 1300, heightMM: 1900, material: 'MS', shellMM: 5, dishMM: 6, pressurized: true, coating: 'ceramic', insulation: 'puf', insulationMM: 50, outerCladding: 'ppgi05', connMaterial: 'MS', manholeSize: '400', manholeMat: 'MS', frame: 5000, valves: 3100, extras: 0, extrasDescription: '', connections: [{ size: '2"', count: 5 }, { size: '1.25"', count: 5 }, { size: '3/4"', count: 2 }] },
  },
  {
    name: '5000L SS304 Press.',
    cfg: { capacityL: 5000, diaMM: 1700, heightMM: 2200, material: 'SS304', shellMM: 4, dishMM: 5, pressurized: true, coating: 'passivation', insulation: 'puf', insulationMM: 50, outerCladding: 'ppgi05', connMaterial: 'SS304', manholeSize: '400', manholeMat: 'SS304', frame: 6000, valves: 3100, extras: 0, extrasDescription: '', connections: [{ size: '2"', count: 4 }, { size: '1.5"', count: 2 }, { size: '3/4"', count: 2 }] },
  },
  {
    name: '10000L SS316L NP Cold 100PUF',
    cfg: { capacityL: 10000, diaMM: 2400, heightMM: 2300, material: 'SS316L', shellMM: 3, dishMM: 3, pressurized: false, coating: 'passivation', insulation: 'puf', insulationMM: 100, outerCladding: 'ss304_1.0', connMaterial: 'SS304', manholeSize: '500', manholeMat: 'SS304', frame: 9000, valves: 0, extras: 0, extrasDescription: '', connections: [{ size: '4"', count: 1 }, { size: '3"', count: 1 }, { size: '2"', count: 4 }] },
  },
  {
    name: '10000L SS2205 NP',
    cfg: { capacityL: 10000, diaMM: 2400, heightMM: 2300, material: 'SS2205', shellMM: 3, dishMM: 3, pressurized: false, coating: 'passivation', insulation: 'puf', insulationMM: 100, outerCladding: 'ss304_1.0', connMaterial: 'SS304', manholeSize: '500', manholeMat: 'SS304', frame: 9000, valves: 0, extras: 0, extrasDescription: '', connections: [{ size: '4"', count: 1 }, { size: '3"', count: 1 }, { size: '2"', count: 4 }] },
  },
];

const labelCls = 'mb-1 block text-sm font-medium text-slate-700';
const inputCls = 'w-full rounded-md border border-slate-300 px-3 py-2 text-sm';
const groupCls = 'rounded-lg border border-slate-200 p-4';
const groupTitleCls = 'mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500';

export default function CustomizedTankCalculatorPage() {
  const navigate = useNavigate();
  const [session, setSession] = useState(() => readStoredSession());

  const [form, setForm] = useState<CustomizedTankInput>(DEFAULTS);
  const [result, setResult] = useState<CustomizedTankResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedQuotationId, setSavedQuotationId] = useState<string | null>(null);
  const [savingQuote, setSavingQuote] = useState(false);

  void proposalsApi; // imported for parity with sibling pages

  function set<K extends keyof CustomizedTankInput>(key: K, value: CustomizedTankInput[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }
  function num(key: keyof CustomizedTankInput, raw: string) {
    set(key, (raw === '' ? 0 : Number(raw)) as any);
  }
  function setConn(idx: number, patch: Partial<Conn>) {
    setForm((f) => ({ ...f, connections: f.connections.map((c, i) => (i === idx ? { ...c, ...patch } : c)) }));
  }
  function addConn() {
    setForm((f) => ({ ...f, connections: [...f.connections, { size: '2"', count: 1 }] }));
  }
  function removeConn(idx: number) {
    setForm((f) => ({ ...f, connections: f.connections.filter((_, i) => i !== idx) }));
  }

  if (!session) {
    return (
      <div className="mx-auto max-w-2xl p-6 mt-10 text-center">
        <AlertTriangle size={32} className="mx-auto mb-3 text-amber-600" />
        <h2 className="text-lg font-semibold text-slate-900">No active meeting session</h2>
        <p className="mt-1 text-slate-600">
          The Customized Tank builder is only available inside a Meeting Mode session.
          Start one to bind the quote to a lead.
        </p>
        <button
          type="button"
          onClick={() => navigate('/sales/meeting/start')}
          className="mt-4 rounded-md bg-slate-900 px-5 py-2 text-white hover:bg-slate-800"
        >
          Initiate Meeting Mode
        </button>
      </div>
    );
  }

  const sess = session;

  async function ensureToken(): Promise<string> {
    if (sess.token) return sess.token;
    const fresh = await customizedTankApi.startSession(sess.leadId);
    const updated = { ...sess, token: fresh.token, sessionId: fresh.sessionId, expiresAt: fresh.expiresAt };
    writeStoredSession(updated);
    setSession(updated);
    return fresh.token;
  }

  async function onCalculate() {
    setLoading(true);
    setError(null);
    setSavedQuotationId(null);
    try {
      const token = await ensureToken();
      const res = await customizedTankApi.calculate(form, token);
      setResult(res);
    } catch (e: any) {
      setError(e?.response?.data?.details?.message || e?.message || 'Calculation failed');
    } finally {
      setLoading(false);
    }
  }

  async function onSaveQuotation() {
    if (!result) return;
    setSavingQuote(true);
    try {
      const token = await ensureToken();
      const outcome = await customizedTankApi.saveQuotation(form, result, token);
      setSavedQuotationId(outcome.id);
    } catch (e: any) {
      setError(e?.response?.data?.details?.message || e?.message || 'Save failed');
    } finally {
      setSavingQuote(false);
    }
  }

  function applyPreset(cfg: CustomizedTankInput) {
    setForm({ ...cfg });
    setResult(null);
    setSavedQuotationId(null);
  }

  return (
    <div className="mx-auto max-w-5xl p-4 md:p-6">
      <h1 className="text-xl font-semibold text-slate-900">Customized Tank Builder</h1>
      <p className="mt-1 text-sm text-slate-500">
        Configure a custom tank BOQ to generate a priced quote and Technical Data Sheet.
      </p>

      {/* Presets */}
      <div className="mt-4 flex flex-wrap gap-2">
        {PRESETS.map((p) => (
          <button
            key={p.name}
            type="button"
            onClick={() => applyPreset(p.cfg)}
            className="rounded-full border border-emerald-600 px-3 py-1 text-xs text-emerald-700 hover:bg-emerald-600 hover:text-white"
          >
            {p.name}
          </button>
        ))}
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {/* 1. Geometry */}
        <section className={groupCls}>
          <h2 className={groupTitleCls}>Geometry</h2>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Capacity (L)</label>
              <input type="number" className={inputCls} value={form.capacityL} onChange={(e) => num('capacityL', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Inner dia (mm)</label>
              <input type="number" className={inputCls} value={form.diaMM} onChange={(e) => num('diaMM', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Inner height (mm)</label>
              <input type="number" className={inputCls} value={form.heightMM} onChange={(e) => num('heightMM', e.target.value)} />
            </div>
          </div>
        </section>

        {/* 2. Material & Build */}
        <section className={groupCls}>
          <h2 className={groupTitleCls}>Material &amp; Build</h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Material</label>
              <select className={inputCls} value={form.material} onChange={(e) => set('material', e.target.value as any)}>
                {MATERIALS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.pressurized} onChange={(e) => set('pressurized', e.target.checked)} />
                Pressurized
              </label>
            </div>
            <div>
              <label className={labelCls}>Shell (mm)</label>
              <input type="number" step="0.5" className={inputCls} value={form.shellMM} onChange={(e) => num('shellMM', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Dish (mm)</label>
              <input type="number" step="0.5" className={inputCls} value={form.dishMM} onChange={(e) => num('dishMM', e.target.value)} />
            </div>
          </div>
        </section>

        {/* 3. Coating & Insulation */}
        <section className={groupCls}>
          <h2 className={groupTitleCls}>Coating &amp; Insulation</h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Coating</label>
              <select className={inputCls} value={form.coating} onChange={(e) => set('coating', e.target.value as any)}>
                {COATINGS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Insulation</label>
              <select className={inputCls} value={form.insulation} onChange={(e) => set('insulation', e.target.value as any)}>
                {INSULATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Insulation (mm)</label>
              <input type="number" className={inputCls} value={form.insulationMM} onChange={(e) => num('insulationMM', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Outer cladding</label>
              <select className={inputCls} value={form.outerCladding} onChange={(e) => set('outerCladding', e.target.value as any)}>
                {CLADDINGS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
        </section>

        {/* 4. Connections & Fittings */}
        <section className={groupCls}>
          <h2 className={groupTitleCls}>Connections &amp; Fittings</h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Conn. material</label>
              <select className={inputCls} value={form.connMaterial} onChange={(e) => set('connMaterial', e.target.value as any)}>
                {CONN_MATERIALS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Manhole size (mm)</label>
              <select className={inputCls} value={form.manholeSize ?? ''} onChange={(e) => set('manholeSize', (e.target.value || null) as any)}>
                {MANHOLE_SIZES.map((s) => <option key={s} value={s}>{s === '' ? 'None' : s}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Manhole material</label>
              <select className={inputCls} value={form.manholeMat} onChange={(e) => set('manholeMat', e.target.value as any)}>
                {MANHOLE_MATS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-3">
            <div className="mb-1 flex items-center justify-between">
              <label className={labelCls}>Connections (nozzles / heater ports)</label>
              <button type="button" onClick={addConn} className="text-xs font-medium text-emerald-700 hover:underline">+ Add connection</button>
            </div>
            <div className="space-y-2">
              {form.connections.length === 0 && <p className="text-xs text-slate-400">No connections added.</p>}
              {form.connections.map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <select className={inputCls} value={c.size} onChange={(e) => setConn(i, { size: e.target.value })}>
                    {CONN_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <input type="number" min={1} className={inputCls} value={c.count} onChange={(e) => setConn(i, { count: Number(e.target.value) || 1 })} />
                  <button type="button" onClick={() => removeConn(i)} className="rounded-md border border-slate-300 px-2 py-1 text-slate-500 hover:bg-slate-100">×</button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 5. Commercials */}
        <section className={`${groupCls} md:col-span-2`}>
          <h2 className={groupTitleCls}>Commercials</h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div>
              <label className={labelCls}>Frame (₹)</label>
              <input type="number" className={inputCls} value={form.frame} onChange={(e) => num('frame', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Safety valves (₹)</label>
              <input type="number" className={inputCls} value={form.valves} onChange={(e) => num('valves', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Extras (₹)</label>
              <input type="number" className={inputCls} value={form.extras} onChange={(e) => num('extras', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Extras description</label>
              <input type="text" className={inputCls} value={form.extrasDescription ?? ''} onChange={(e) => set('extrasDescription', e.target.value)} placeholder="e.g. immersion heaters" />
            </div>
          </div>
        </section>
      </div>

      {error && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>
      )}

      <div className="mt-4 flex gap-3">
        <button
          type="button"
          onClick={onCalculate}
          disabled={loading}
          className="rounded-md bg-slate-900 px-5 py-2 text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {loading ? 'Calculating…' : 'Calculate'}
        </button>
      </div>

      {/* Result — ONLY the Final B2B Price + the TDS. No cost build-up. */}
      {result && (
        <div className="mt-6 rounded-lg border border-slate-200 p-5">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500">Final B2B Price (GST 18% extra)</div>
              <div className="text-3xl font-bold text-slate-900">{formatINR(result.customerPrice)}</div>
            </div>
            <div className="text-sm text-slate-500">{result.tds.modelName}</div>
          </div>

          {/* KPI tiles */}
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            {result.tds.kpi.map((k, i) => (
              <div key={i} className="rounded-md bg-slate-50 p-3 text-center">
                <div className="text-sm font-semibold text-slate-900">{k.value}</div>
                <div className="text-xs text-slate-500">{k.label}</div>
              </div>
            ))}
          </div>

          {/* TDS table */}
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                  <th className="py-2 pr-2">#</th>
                  <th className="py-2 pr-4">Description</th>
                  <th className="py-2">Specifications</th>
                </tr>
              </thead>
              <tbody>
                {result.tds.specs.map((row, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-2 pr-2 text-slate-400">{row.sr}</td>
                    <td className="py-2 pr-4 font-medium text-slate-700">{row.description}</td>
                    <td className="py-2 text-slate-600">{row.specifications}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={onSaveQuotation}
              disabled={savingQuote || !!savedQuotationId}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {savedQuotationId ? 'Quotation saved' : savingQuote ? 'Saving…' : 'Save Quotation'}
            </button>
            {savedQuotationId && (
              <ConvertToProposalButton
                calculatorKey="customized_tank"
                quotationId={savedQuotationId}
                customerName={sess.leadLabel ?? undefined}
                leadId={sess.leadId ?? undefined}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
